/**
 * useCheckoutSubmit Hook
 * Handle submit order logic (upload files, patch data, submit)
 */

import { useToast } from '@chakra-ui/react';
import { PatchDataWrapper, PostDataWrapper } from '@diamond/shared/data-access';
import { showToast } from '@diamond/shared/ui';
import { apiInstance, trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import type {
  CheckoutFormData,
  EcommerceItem,
  SubmitOrderPayload,
} from '@diamond/sol-buyer/utils';
import {
  formatProductsForTracking,
  handleCheckoutError,
  handleCheckoutSuccess,
} from '@diamond/sol-buyer/utils';
import { useRouter } from 'next/router';
import { useState } from 'react';

import { useAuthStore } from '../../stores/useAuthStore';
import { useCartStore } from '../../stores/useCartStore';

export interface UseCheckoutSubmitReturn {
  submitOrder: (
    formData: CheckoutFormData,
    voucherCode?: string
  ) => Promise<void>;
  isSubmitting: boolean;
  submitError: string | null;
}

/**
 * Custom hook untuk handle checkout submission
 *
 * @example
 * const { submitOrder, isSubmitting } = useCheckoutSubmit(metaId, warehouse);
 */
export const useCheckoutSubmit = (
  metaId: string,
  warehouse: string
): UseCheckoutSubmitReturn => {
  const router = useRouter();
  const toast = useToast();
  const { access_token: token } = useAuthStore();
  const cartStore = useCartStore();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { patchData } = PatchDataWrapper('/order/meta', token, 'buyer');
  const { postData } = PostDataWrapper(
    '/order/purchase-order-document/upload',
    token,
    true,
    'buyer'
  );

  // Wrapper function to match ToastFunction signature
  const toastFn = (
    _toast: any,
    status: 'success' | 'error' | 'warning' | 'info',
    message: string
  ) => {
    showToast(toast, status as any, message);
  };

  /**
   * Upload PO documents
   */
  const uploadDocuments = async (files: File[]): Promise<string[]> => {
    if (!files || files.length === 0) return [];

    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`file-${index}`, file, file.name);
    });

    try {
      const config = {
        headers: {
          authorization: `Bearer ${token}`,
        },
      };

      const response = await postData(formData, config);
      return (response as { urls?: string[] })?.urls || [];
    } catch (error: unknown) {
      handleCheckoutError(
        error,
        'upload-document',
        toastFn,
        toast,
        'Gagal upload dokumen',
        metaId
      );
      throw error;
    }
  };

  /**
   * Patch order data (update delivery info, payment method, etc.)
   */
  const patchOrderData = async (
    formData: CheckoutFormData,
    documentUrls: string[]
  ): Promise<void> => {
    try {
      await patchData(
        {
          purchase_order_docs: documentUrls,
          delivery_date: formData.delivery_date,
          delivery_note: formData.delivery_note,
          delivery_option: formData.delivery_option,
          payment_method: formData.payment_method,
          ship_to_contact: {
            name: formData.ship_to_contact.name,
            telp: formData.ship_to_contact.telp,
          },
        },
        metaId
      );
    } catch (error: unknown) {
      handleCheckoutError(
        error,
        'patch-order',
        toastFn,
        toast,
        'Gagal update order',
        metaId
      );
      throw error;
    }
  };

  /**
   * Submit order to backend
   */
  const doSubmitOrder = async (
    payload: SubmitOrderPayload,
    formData: CheckoutFormData
  ): Promise<void> => {
    try {
      const response = await apiInstance(token).post(
        `/order/meta/${metaId}/submit`,
        payload
      );

      if (response.status === 200 || response.status === 201) {
        // Reset cart
        cartStore.reset([]);

        // Prepare tracking items
        const trackingItems: EcommerceItem[] = [];
        if (formData.items?.length > 0) {
          formData.items.forEach((item) => {
            const formatted = formatProductsForTracking(
              item.products,
              warehouse
            );
            trackingItems.push(...formatted);
          });
        }

        // Track purchase event
        trackEvent(TRACKING_EVENTS.PURCHASE, {
          transaction_id: formData.transaction_id,
          currency: 'IDR',
          value: formData.total || 0,
          total_discount: formData.saved_total || 0,
          items: trackingItems,
          section_name: TRACKING_PAGES.CHECKOUT,
        });

        // Track payment info event
        trackEvent(TRACKING_EVENTS.ADD_PAYMENT_INFO, {
          currency: 'IDR',
          value: formData.total || 0,
          payment_type: formData.payment_method,
          warehouse,
          meta_id: metaId,
          items: trackingItems,
          section_name: TRACKING_PAGES.CHECKOUT,
        });

        // Show success message
        handleCheckoutSuccess(toastFn, toast, 'Berhasil Checkout!');

        // Redirect to order detail
        await router.push({
          pathname: `/order-detail/${metaId}`,
          query: { warehouse },
        });
      }
    } catch (error: unknown) {
      handleCheckoutError(
        error,
        'submit-order',
        toastFn,
        toast,
        'Gagal submit order',
        metaId
      );
      router.push('/cart');
      throw error;
    }
  };

  /**
   * Main submit function
   * @param formData - Checkout form data
   * @param voucherCode - Optional voucher code to apply (SAP voucher code)
   */
  const submitOrder = async (
    formData: CheckoutFormData,
    voucherCode?: string
  ): Promise<void> => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // 1. Upload documents (if any)
      let documentUrls: string[] = [];
      if (
        formData.purchase_order_document &&
        formData.purchase_order_document.length > 0
      ) {
        const files = Array.from(formData.purchase_order_document);
        documentUrls = await uploadDocuments(files);
      }

      // 2. Patch order data
      await patchOrderData(formData, documentUrls);

      // 3. Submit order
      const payload: SubmitOrderPayload = {
        id: metaId,
        warehouse,
        ...(voucherCode && { sap_voucher_code: voucherCode }),
      };
      await doSubmitOrder(payload, formData);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Gagal submit order';
      setSubmitError(errorMessage);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitOrder,
    isSubmitting,
    submitError,
  };
};
